import { Box, Button, Card, CardContent, Divider, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useErrorDisplay } from '../../components/ErrorDisplay';
import BusySpinner from '../../components/BusySpinner';
import PanelSegment from '../../components/PanelSegment';
import PageTitle from '../../components/PageTitle';
import useViewportMode from '../../hooks/useViewportMode';
import { useAccessInfo } from '../../contexts/AccessTokenProvider';
import { Securables } from '../../Enums';
import ProducerPortalClient from '../../apiClients/ProducerPortalClient';
import { ProducerDashboardSummary } from '../../models/producer';

const money = (value?: number) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format((value || 0) / 100);

const StatCard = ({ label, value, helper }: { label: string; value: string; helper?: string }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="overline" color="text.secondary">{label}</Typography>
                <Typography variant="h5" sx={{ mt: 0.5, mb: 0.5 }}><strong>{value}</strong></Typography>
                {helper && <Typography color="text.secondary" variant="body2">{helper}</Typography>}
            </CardContent>
        </Card>
    </Grid>
);

const ProducerDashboardPage = () => {
    const { mode, isDesktop } = useViewportMode();
    const { accessInfo, hasRole } = useAccessInfo();
    const history = useHistory();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState(false);
    const [summary, setSummary] = useState<ProducerDashboardSummary>(null);

    const isProducerAdmin = hasRole(Securables.producer_admin, true);
    const financial = summary?.Billing;

    const loadDashboard = async () => {
        if (!accessInfo.selectedProducerCompanyId)
            return;

        try {
            setBusy(true);
            const data = await new ProducerPortalClient().GetDashboard(accessInfo.selectedProducerCompanyId);
            setSummary({
                ...data,
                ...(isProducerAdmin ? null : { Billing: undefined }),
            });
        } catch (error) {
            setError(error, 'Failed to load producer dashboard');
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessInfo.selectedProducerCompanyId]);

    return <Box data-viewport-mode={mode}>
        <ErrorDisplay />
        <BusySpinner busy={busy}>
            <PanelSegment
                titleContent={<PageTitle title="Producer portal" />}
                toolbar={
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Button variant="outlined" onClick={() => history.push('/ProducerPortal/ClientCompanies')} sx={{ minHeight: isDesktop ? undefined : 44 }}>Clients</Button>
                        {isProducerAdmin && <Button variant="outlined" onClick={() => history.push('/ProducerPortal/Team')} sx={{ minHeight: isDesktop ? undefined : 44 }}>Team</Button>}
                        {isProducerAdmin && <Button variant="outlined" onClick={() => history.push('/ProducerPortal/Reports')} sx={{ minHeight: isDesktop ? undefined : 44 }}>Reports</Button>}
                        {isProducerAdmin && <Button variant="outlined" onClick={() => history.push('/ProducerPortal/Payouts')} sx={{ minHeight: isDesktop ? undefined : 44 }}>Payouts</Button>}
                    </Stack>
                }
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>{accessInfo.selectedProducerCompanyName || 'Producer workspace'}</Typography>
                    <Grid container spacing={2}>
                        <StatCard label="Role" value={summary?.MembershipRole || '-'} />
                        <StatCard label="Active clients" value={`${summary?.ActiveClientCount ?? 0}`} />
                        <StatCard label="Active billed employees" value={`${summary?.ActiveBilledEmployeeCount ?? 0}`} />
                        <StatCard label="Client invites" value={`${summary?.PendingClientInvitations ?? 0}`} />
                        <StatCard label="Team invites" value={`${summary?.PendingTeamInvitations ?? 0}`} />
                    </Grid>

                    {isProducerAdmin && (
                        <>
                            <Box sx={{ mt: 2.5 }}>
                                <Divider />
                            </Box>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Financial visibility</Typography>
                            <Grid container spacing={2}>
                                <StatCard label="Monthly earned (paid)" value={money(financial?.MonthlyEarningsCents)} />
                                <StatCard label="Projected earnings" value={money(financial?.ProjectedEarningsCents)} />
                                <StatCard label="Pending payout" value={money(financial?.PendingPayoutCents)} />
                                <StatCard label="Statement status" value={financial?.StatementStatus || 'Not ready'} />
                                <StatCard label="Settlement status" value={financial?.LastPaymentStatus || 'Not available'} />
                            </Grid>
                        </>
                    )}

                    {!isProducerAdmin && (
                        <Box sx={{ mt: 2.5 }}>
                            <Typography color="text.secondary">
                                Financial totals are managed by Producer Admin users.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </PanelSegment>
        </BusySpinner>
    </Box>;
};

export default ProducerDashboardPage;
